import React, { useMemo } from 'react'
import { flatJoin, numberFormat } from 'shared/util'

import {
  Stack, Typography, Tooltip,
  Table, TableContainer, TableBody, TableCell, TableHead,
  TableRow, TableRowProps
} from '@mui/material'

import { TableVirtuoso } from 'react-virtuoso'

import { RectangleRounded as DpcMarkIcon } from '@mui/icons-material'

import { SisetusDpcResult } from 'interface/dpc/types'
import { SelectedDpc } from '../types'
import { SisetuResult } from 'interface/sisetu'

type VirtusoTableRowImplimentProps = {
  item: { sisetu: SisetuResult }
  onSisetuClick: (sisetuCode: string) => void
} & TableRowProps
const VirtusoTableRowImpliment = ({
  item: { sisetu },
  onSisetuClick,
  ...others
}: VirtusoTableRowImplimentProps) => {
  return (
    <TableRow
      {...others}
      hover
      sx={{ cursor: 'pointer' }}
      onClick={() => {
        onSisetuClick(sisetu.sisetuCode)
      }}
    />
  )
}

type DpcResultTableProps = {
  results: SisetusDpcResult[]
  selectedDpcList: SelectedDpc[]
  calcTotalCase: (
    result: SisetusDpcResult,
    dpcCode: string
  ) => number | undefined
  onSisetuClick: (sisetuCode: string) => void
}
export const DpcResultTable = ({
  results,
  selectedDpcList,
  calcTotalCase,
  onSisetuClick,
}: DpcResultTableProps) => {
  const records = useMemo(
    () => {
      const recordMap: { [sisetuCode: string]: {
        sisetu: SisetuResult
        result: { [dpcCode: string]: number }
      }} = {}
      for(const result of results) {
        const sisetuCode = result.sisetu.sisetuCode
        let record = recordMap[sisetuCode]
        if (!record) {
          record = {
            sisetu: result.sisetu,
            result: {}
          }
          recordMap[sisetuCode] = record
        }
        for(const { dpcCode } of selectedDpcList) {
          const value = calcTotalCase(result, dpcCode)
          if (value === undefined || value === null) continue
          record.result[dpcCode] = (record.result[dpcCode] ?? 0) + value
        }
      }
      const records = Object.values(recordMap)
      records.sort((a, b) => {
        for(const { dpcCode } of selectedDpcList) {
          const valueA = a.result[dpcCode] ?? -1
          const valueB = b.result[dpcCode] ?? -1
          if (valueA === valueB) continue
          return valueB - valueA
        }
        return 0
      })
      return records
    },
    [results, selectedDpcList, calcTotalCase]
  )
  return (
    <TableVirtuoso
      components={{
        Scroller: React.forwardRef(
          function VirtuosoTableContainerImpliment(props, ref) {
            return <TableContainer {...props} ref={ref} />
          }
        ),
        Table: (props) => (
          <Table
            {...props}
            size="small"
            style={{
              borderCollapse: 'separate',
              width: '100%'
            }}
          />
        ),
        TableHead: React.forwardRef(
          function VirtusoTableHeadImpliment(props, ref) {
            return (
              <TableHead {...props} ref={ref} sx={{ background: 'white' }} />
            )
          }
        ),
        TableRow: (props) => VirtusoTableRowImpliment({ onSisetuClick, ...props }),
        TableBody: React.forwardRef(
          function VirtusoTableBodyImpliment(props, ref) {
            return <TableBody {...props} ref={ref} />
          }
        ),
      }}
      style={{
        width: '100%',
        height: '100%',
      }}
      data={records}
      fixedHeaderContent={() => (
        <TableRow>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>#</TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>所在地</TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>施設名</TableCell>

          {selectedDpcList.map((dpc, index) => (
            <TableCell
              sx={{ whiteSpace: 'nowrap' }}
              key={`table_${dpc.dpcCode}_${index}`}
            >
              <Tooltip title={dpc.diseaseName ?? `column-${index}`}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <DpcMarkIcon fontSize="small" sx={{ color: dpc.color }} />
                  <Typography variant='body2' whiteSpace='nowrap' textOverflow='ellipsis' overflow='hidden' maxWidth='5rem'>
                    {dpc.diseaseName ?? `column-${index}`}
                  </Typography>
                </Stack>
              </Tooltip>
            </TableCell>
          ))}
          <TableCell width="100%" padding='none'/>
        </TableRow>
      )}
      itemContent={(index, { sisetu, result }) => (
        <>
          <TableCell align='right'>{index + 1}</TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {flatJoin(' ', sisetu.juusyoKenName, sisetu.cityName)}
          </TableCell>
          <TableCell
            sx={{ width: '100%' }}
            onClick={() => onSisetuClick(sisetu.sisetuCode ?? '')}
          >
            <Typography variant="body2" component="span">{sisetu.sisetuName}</Typography>
            {(sisetu.keieitai?.name || sisetu.bedCount?.total) && (
              <Typography variant="caption" color="textDisabled" component="span">
                （{flatJoin(
                  ' ',
                  sisetu.keieitai?.name,
                  sisetu.bedCount?.total ? `${sisetu.bedCount.total}床` : null  
                )}）
              </Typography>						
            )}
          </TableCell>

          {selectedDpcList.map((dpc, index) => {
            const value = result[dpc.dpcCode]
            return (value === undefined || value === null) ? (
              <TableCell
                sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                key={`${sisetu.sisetuCode}_${dpc.dpcCode}_${index}`}
              >
                -
              </TableCell>
            ) : (
              <TableCell
                sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}
                key={`${sisetu.sisetuCode}_${dpc.dpcCode}_${index}`}
              >
                {value ? `${numberFormat(value)} 件` : ''}
              </TableCell>
            )
          })}
          <TableCell width="100%" padding='none'/>
        </>
      )}
    />
  )
}
